/**
 * Shared date/number formatting. The only place the portal formats dates —
 * page-local `formatDate`/`formatDateTime` helpers are not allowed.
 * All formatters honor the browser locale and render an em dash for absent values.
 *
 * Dates show in the viewer's zone, except where the stored moment stands for a
 * calendar day in a fixed zone (license key expiry is end-of-day UTC). Pass
 * `timeZone` there and write the zone beside the value: "Oct 4, 2026 (UTC)".
 */

export interface DateFormatOptions {
  /** IANA zone, e.g. 'UTC'. Absent means the viewer's own zone. */
  timeZone?: string;
}

export const EMPTY_VALUE = '—';

const isValidDate = (date: Date): boolean => !Number.isNaN(date.getTime());

/**
 * "Mar 4, 2026". In a table, record-history columns (Created, Updated, Expires,
 * Joined, Invited) render through `DateCell` (components/Timestamp.tsx), which
 * calls this and keeps the full date-time on hover — don't call it in a render.
 */
export function formatDate(
  value: string | number | Date | null | undefined,
  options?: DateFormatOptions,
): string {
  if (value === null || value === undefined || value === '') return EMPTY_VALUE;
  const date = value instanceof Date ? value : new Date(value);
  if (!isValidDate(date)) return EMPTY_VALUE;
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...(options?.timeZone ? { timeZone: options.timeZone } : {}),
  });
}

/**
 * "Mar 4, 2026, 2:41 PM" — audit rows (License Keys Created), dialog metadata
 * and the hover title of every `DateCell` / `RelativeTime`.
 */
export function formatDateTime(
  value: string | number | Date | null | undefined,
  options?: DateFormatOptions,
): string {
  if (value === null || value === undefined || value === '') return EMPTY_VALUE;
  const date = value instanceof Date ? value : new Date(value);
  if (!isValidDate(date)) return EMPTY_VALUE;
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...(options?.timeZone ? { timeZone: options.timeZone } : {}),
  });
}

/**
 * "4 minutes ago" / "in 3 days" — recency (Last seen, Last sign-in). In a table
 * render it through `RelativeTime`, which keeps the exact date-time on hover.
 */
export function formatRelativeTime(value: string | number | Date | null | undefined): string {
  if (value === null || value === undefined || value === '') return EMPTY_VALUE;
  const date = value instanceof Date ? value : new Date(value);
  if (!isValidDate(date)) return EMPTY_VALUE;

  const diffMs = date.getTime() - Date.now();
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  const abs = Math.abs(diffMs);

  const MINUTE = 60_000;
  const HOUR = 60 * MINUTE;
  const DAY = 24 * HOUR;

  if (abs < MINUTE) return rtf.format(Math.round(diffMs / 1000), 'second');
  if (abs < HOUR) return rtf.format(Math.round(diffMs / MINUTE), 'minute');
  if (abs < DAY) return rtf.format(Math.round(diffMs / HOUR), 'hour');
  if (abs < 30 * DAY) return rtf.format(Math.round(diffMs / DAY), 'day');
  return formatDate(date);
}

/**
 * "26.70000, -80.05000" — a point as decimal degrees. 5 decimals in dialogs
 * (about a metre), 4 in compact table cells. Either half missing or not a
 * finite number is no point at all: EMPTY_VALUE, never "0.00000".
 */
export function formatCoordinates(
  lat: number | null | undefined,
  lng: number | null | undefined,
  precision = 5,
): string {
  if (typeof lat !== 'number' || typeof lng !== 'number') return EMPTY_VALUE;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return EMPTY_VALUE;
  return `${lat.toFixed(precision)}, ${lng.toFixed(precision)}`;
}

const plural = (count: number, unit: string): string =>
  `${formatNumber(count)} ${unit}${count === 1 ? '' : 's'}`;

/**
 * "1 hour 30 minutes", "5 minutes", "1 day 1 hour" — a length of time given in
 * MINUTES, exact and never rounded: days, then hours, then minutes, zero parts
 * left out. 0 is a known value ("0 minutes"); absent or NaN is EMPTY_VALUE.
 * The only duration formatter: a table cell and a dialog must read the same.
 */
export function formatDuration(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || !Number.isFinite(minutes)) return EMPTY_VALUE;
  const total = Math.max(0, Math.trunc(minutes));
  if (total === 0) return plural(0, 'minute');
  const days = Math.floor(total / 1440);
  const hours = Math.floor((total % 1440) / 60);
  const mins = total % 60;
  const parts: string[] = [];
  if (days) parts.push(plural(days, 'day'));
  if (hours) parts.push(plural(hours, 'hour'));
  if (mins) parts.push(plural(mins, 'minute'));
  return parts.join(' ');
}

/** "1 day", "30 days" — a length of time given in DAYS. Absent is EMPTY_VALUE. */
export function formatDays(days: number | null | undefined): string {
  if (days === null || days === undefined || !Number.isFinite(days)) return EMPTY_VALUE;
  return plural(days, 'day');
}

/** "12,408" — counts with locale grouping; em dash for null/undefined. */
export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EMPTY_VALUE;
  return value.toLocaleString();
}

/**
 * Human label for a tier slug.
 *
 * A FALLBACK ONLY — the real label is `tierLabel`, which the API reads from
 * `organization_tiers.display_name`. This exists for the deploy window (the
 * portal ships before the migration, so it must render the old `free` /
 * `professional` labels too) and for any older response that omits the field.
 *
 * Note it does the job CSS `capitalize` cannot: that renders `tier_1` as
 * `Tier_1`.
 */
export function formatTier(slug: string | null | undefined): string {
  if (!slug) return EMPTY_VALUE;
  return slug
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}
